// =============================================================================
// sessionTimeout.ts — admin-configurable idle-logout periods (per audience)
// =============================================================================
// The auto-logout period is editable at Manage → System Setup → Config, with a
// SEPARATE value for DFV Services (shelter users) and Zephyr staff (all internal
// users). Stored in portal_config under SESSION_TIMEOUT_KEY as
// { shelter_minutes, staff_minutes } = minutes of inactivity before logout. The
// "you're about to be logged out" warning shows WARN_LEAD_MINUTES before that.
//
// Pure + framework-free so it can be unit-tested and reused by the config screen
// and the idle-logout hook (src/hooks/useInactivityLogout.ts).
// =============================================================================

import { type AccessUser, isInternalUser } from "../app/access";

export const SESSION_TIMEOUT_KEY = "session_timeout";

// Defaults preserve the historical behaviour (30-min logout, warn at 25).
export const DEFAULT_SHELTER_MINUTES = 30;
export const DEFAULT_STAFF_MINUTES = 30;
// The expiry warning appears this many minutes before the logout.
export const WARN_LEAD_MINUTES = 5;
// Guard rails for the editable values (1 min .. 24 h).
export const MIN_MINUTES = 1;
export const MAX_MINUTES = 1440;

export interface SessionTimeout {
  shelter_minutes: number;
  staff_minutes: number;
}

function clampMinutes(n: unknown, fallback: number): number {
  // null/undefined/"" would coerce to 0 via Number(), so bail to the fallback.
  if (n === null || n === undefined || n === "") return fallback;
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  const r = Math.round(x);
  if (r < MIN_MINUTES) return MIN_MINUTES;
  if (r > MAX_MINUTES) return MAX_MINUTES;
  return r;
}

// Tolerate a missing/absent key or a malformed value → the defaults.
export function normalizeSessionTimeout(value: unknown): SessionTimeout {
  const v = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  return {
    shelter_minutes: clampMinutes(v.shelter_minutes, DEFAULT_SHELTER_MINUTES),
    staff_minutes: clampMinutes(v.staff_minutes, DEFAULT_STAFF_MINUTES),
  };
}

// The idle-logout minutes that apply to a given user: staff value for any
// internal Zephyr user, shelter value for a DFV Service caseworker.
export function logoutMinutesForUser(user: AccessUser | null | undefined, value: unknown): number {
  const t = normalizeSessionTimeout(value);
  return isInternalUser(user) ? t.staff_minutes : t.shelter_minutes;
}

// The warn + logout timers (in ms) for a user, given the stored config value.
// Warning leads the logout by WARN_LEAD_MINUTES, clamped so it's always at least
// 1 minute and always strictly before the logout.
export function timersForUser(
  user: AccessUser | null | undefined,
  value: unknown
): { warnMs: number; logoutMs: number } {
  const logoutMin = logoutMinutesForUser(user, value);
  const warnMin = Math.max(1, Math.min(logoutMin - 1, logoutMin - WARN_LEAD_MINUTES));
  return { warnMs: warnMin * 60 * 1000, logoutMs: logoutMin * 60 * 1000 };
}

// How many minutes the user actually has left when the warning banner appears.
// This is WARN_LEAD_MINUTES only when the configured period is long enough; on a
// short period the clamp above pulls the warning closer to the logout, so the
// real lead is smaller. The banner used to hard-code "5 minutes" and therefore
// lied whenever an admin set a period of 6 minutes or less. Finding B16 item 6.
export function warnLeadMinutesForUser(
  user: AccessUser | null | undefined,
  value: unknown
): number {
  const logoutMin = logoutMinutesForUser(user, value);
  const warnMin = Math.max(1, Math.min(logoutMin - 1, logoutMin - WARN_LEAD_MINUTES));
  return logoutMin - warnMin;
}
